/**
 * Message Author Resolution Utility
 * 
 * Centralizes logic for determining message authorship across all messaging surfaces.
 * Uses author_user_id as the canonical source of truth.
 * 
 * Key Rules:
 * - author_user_id is the canonical field (server-side, cannot be spoofed)
 * - sender_id is legacy and only used as fallback for old messages
 * - on_behalf_of_couple_id indicates a couple message
 * - Never infer author identity from sender_id for display decisions
 */

/**
 * Resolve the display author of a message
 * @param {Object} message - The message object
 * @param {Object} options - Options for resolution
 * @param {string} options.currentUserId - The current logged-in user's ID
 * @param {Object} options.authorProfiles - Map of user_id -> profile data
 * @returns {Object} { displayName, isCoupleMessage, isFromCurrentUser, forCoupleLabel }
 */
export function resolveMessageAuthor(message, options = {}) {
  const { currentUserId, authorProfiles = {} } = options;
  
  // Get the canonical author ID (prefer author_user_id, fallback to sender_id for legacy)
  const authorId = message?.author_user_id || message?.authorUserId || message?.sender_id;
  
  // Determine if this is a couple message
  const isCoupleMessage = Boolean(message?.on_behalf_of_couple_id || message?.onBehalfOfCoupleId);
  
  // Determine if the message is from the current user
  const isFromCurrentUser = authorId === currentUserId;
  
  // Resolve display name
  let displayName = "Someone";
  
  // First, check if message already has author_name (from backend)
  if (message?.author_name || message?.authorName) {
    displayName = message.author_name || message.authorName;
  } 
  // Then check the profiles map
  else if (authorId && authorProfiles[authorId]) {
    const profile = authorProfiles[authorId];
    displayName = profile.names?.[0] || profile.display_name || profile.name || "Someone";
  }
  // Fallback to sender_name if available (legacy)
  else if (message?.sender_name || message?.senderName) {
    displayName = message.sender_name || message.senderName;
  }
  
  // Generate the "(for the couple)" label if applicable
  const forCoupleLabel = isCoupleMessage ? "(for the couple)" : "";
  
  return {
    authorId,
    displayName,
    isCoupleMessage,
    isFromCurrentUser,
    forCoupleLabel,
  };
}

/**
 * Format a message preview for conversation lists
 * Shows "Author: Message text..." or "Author (for the couple): Message text..."
 * @param {Object} message - The last message object
 * @param {Object} options - Options for formatting
 * @param {string} options.currentUserId - The current logged-in user's ID
 * @param {Object} options.authorProfiles - Map of user_id -> profile data
 * @param {number} options.maxLength - Max length for the message text (default 50)
 * @returns {string} Formatted preview string
 */
export function formatMessagePreview(message, options = {}) {
  if (!message) return "Start the conversation ✨";
  
  // Voice message preview (check both type and audio_url for safety)
  if (message.type === "audio" || message.audio_url) {
    return "🎤 Voice message";
  }
  
  const { currentUserId, authorProfiles = {}, maxLength = 50 } = options;
  const { displayName, isCoupleMessage, isFromCurrentUser } = resolveMessageAuthor(message, { currentUserId, authorProfiles });
  
  // Get message text
  const text = message.text || message.content || "";
  const truncatedText = text.length > maxLength ? text.slice(0, maxLength) + "…" : text;
  
  // Format based on author
  if (isFromCurrentUser) {
    return `You: ${truncatedText}`;
  }
  
  if (isCoupleMessage) {
    return `${displayName} (for the couple): ${truncatedText}`;
  }
  
  return `${displayName}: ${truncatedText}`;
}

/**
 * Format author label for thread message bubbles
 * Shows author name with optional "(for the couple)" suffix
 * @param {Object} message - The message object
 * @param {Object} options - Options for formatting
 * @returns {Object|null} { label, style } or null if no label needed
 */
export function formatThreadAuthorLabel(message, options = {}) {
  const { currentUserId, authorProfiles = {} } = options;
  const { displayName, isCoupleMessage, isFromCurrentUser } = resolveMessageAuthor(message, { currentUserId, authorProfiles });
  
  // Don't show author label for own messages
  if (isFromCurrentUser) {
    return null;
  }
  
  // For couple messages, always show author with "(for the couple)"
  if (isCoupleMessage) {
    return {
      label: `${displayName} (for the couple)`,
      style: "couple",
    };
  }
  
  // For regular messages, no label needed (name is in header)
  return null;
}

/**
 * Get notification text for a new message
 * Used for push notifications and in-app alerts
 * @param {Object} message - The message object
 * @param {Object} options - Options for formatting
 * @returns {string} Notification text like "Alice sent a message"
 */
export function formatMessageNotification(message, options = {}) {
  const { authorProfiles = {} } = options;
  const { displayName, isCoupleMessage } = resolveMessageAuthor(message, { currentUserId: null, authorProfiles });
  
  if (isCoupleMessage) {
    return `${displayName} (from the couple) sent a message`;
  }
  
  return `${displayName} sent a message`;
}

export default {
  resolveMessageAuthor,
  formatMessagePreview,
  formatThreadAuthorLabel,
  formatMessageNotification,
};
